import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Flex,
  Progress,
  Skeleton,
  Space,
  Typography,
  Timeline,
  Card,
  Tag,
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, PhoneOutlined, SwapOutlined, EditOutlined, UserSwitchOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { leadsApi } from '@/core/api/leadsApi';
import type { LeadWithEtag } from '@/core/types/lead';
import { formatDateTime } from '@/core/utils/date';
import { formatNumber, getLeadDisplayName } from '@/core/utils/format';
import { ErrorState } from '@/shared/components/ErrorState';
import { LeadPriorityTag, LeadStatusTag } from '@/shared/components/LeadTags';
import { PermissionGuard } from '@/shared/components/PermissionGuard';
import { leadQueryKeys } from '@/features/leads/hooks/useLeads';
import { activitiesApi } from '@/core/api/activitiesApi';
import { LeadLifecycleModal, type LifecycleMode } from './LeadLifecycleModal';

interface LeadDetailsDrawerProps {
  leadId: string | null;
  onClose: () => void;
  onEdit: (lead: LeadWithEtag) => void;
  onAssign: (lead: LeadWithEtag) => void;
}

export const LeadDetailsDrawer = ({
  leadId,
  onClose,
  onEdit,
  onAssign,
}: LeadDetailsDrawerProps): React.ReactElement => {
  const [lifecycleMode, setLifecycleMode] = useState<LifecycleMode | null>(null);
  const query = useQuery({
    queryKey: leadId ? leadQueryKeys.detail(leadId) : ['leads', 'detail', 'none'],
    queryFn: () => leadsApi.get(leadId as string),
    enabled: Boolean(leadId),
  });
  const detail = query.data;
  const lead = detail?.lead;
  const history = useQuery({ queryKey: ['lead-history', leadId], queryFn: () => leadsApi.history(leadId as string), enabled: Boolean(leadId) });
  const engagements = useQuery({ queryKey: ['lead-engagements', leadId], queryFn: () => leadsApi.engagements(leadId as string), enabled: Boolean(leadId) });
  const activities = useQuery({ queryKey: ['activities', 'lead', leadId], queryFn: () => activitiesApi.list({ leadId: leadId as string, pageSize: 20 }), enabled: Boolean(leadId) });
  const followUpMutation = useMutation({
    mutationFn: () => leadsApi.startFollowUp(leadId as string),
    onSuccess: () => { void query.refetch(); void history.refetch(); void engagements.refetch(); },
  });

  return (
    <Drawer
      open={Boolean(leadId)}
      onClose={onClose}
      width="min(680px, 100vw)"
      title={lead ? getLeadDisplayName(lead) : 'جزئیات سرنخ'}
      destroyOnHidden
      extra={
        detail ? (
          <Space size={[4, 4]} wrap>
            {lead && (lead.status === 'new' || lead.status === 'working') ? <>
              {lead.status === 'new' ? <PermissionGuard permission="leads.update"><Button icon={<PhoneOutlined />} loading={followUpMutation.isPending} onClick={() => followUpMutation.mutate()}>شروع پیگیری</Button></PermissionGuard> : null}
              <PermissionGuard permission="leads.qualify"><Button icon={<CheckCircleOutlined />} onClick={() => setLifecycleMode('qualify')}>تأیید</Button></PermissionGuard>
              <PermissionGuard permission="leads.disqualify"><Button danger icon={<CloseCircleOutlined />} onClick={() => setLifecycleMode('disqualify')}>رد</Button></PermissionGuard>
            </> : null}
            {lead?.status === 'qualified' ? <PermissionGuard permission="leads.convert"><Button type="primary" icon={<SwapOutlined />} onClick={() => setLifecycleMode('convert')}>تبدیل</Button></PermissionGuard> : null}
            <PermissionGuard permission="leads.update">
              <Button icon={<EditOutlined />} onClick={() => onEdit(detail)}>
                ویرایش
              </Button>
            </PermissionGuard>
            <PermissionGuard permission="leads.assign">
              <Button type="primary" icon={<UserSwitchOutlined />} onClick={() => onAssign(detail)}>
                تخصیص
              </Button>
            </PermissionGuard>
          </Space>
        ) : null
      }
    >
      {query.isLoading ? <Skeleton active paragraph={{ rows: 10 }} /> : null}
      {query.isError ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}
      {lead ? (
        <Flex vertical gap={20}>
          <div className="lead-detail-hero">
            <Flex justify="space-between" align="center" gap={16} wrap="wrap">
              <div>
                <Space wrap>
                  <LeadStatusTag status={lead.status} />
                  <LeadPriorityTag priority={lead.priority} />
                </Space>
                <Typography.Title level={3} className="lead-detail-hero__name">
                  {getLeadDisplayName(lead)}
                </Typography.Title>
                <Typography.Text type="secondary">{lead.companyName || 'مشتری حقیقی'}</Typography.Text>
              </div>
              <div className="lead-score-box">
                <Typography.Text type="secondary">امتیاز سرنخ</Typography.Text>
                <Typography.Title level={2}>{formatNumber(lead.score)}</Typography.Title>
                <Progress type="circle" percent={lead.score} size={64} format={() => ''} />
                <Typography.Text className="mt-2 block text-[10px]" type="secondary">تناسب {formatNumber(lead.fitScore || 0)} · تعامل {formatNumber(lead.engagementScore || 0)}</Typography.Text>
              </div>
            </Flex>
          </div>

          {lead.duplicateCheck?.hasPossibleDuplicates ? (
            <Alert
              type="warning"
              showIcon
              title="این سرنخ در زمان ثبت، موارد مشابهی داشته است."
            />
          ) : null}

          <Descriptions bordered column={{ xs: 1, md: 2 }} size="middle">
            <Descriptions.Item label="نام مدیرعامل">{lead.chiefExecutiveName || '—'}</Descriptions.Item>
            <Descriptions.Item label="شماره مدیرعامل">
              <Space><PhoneOutlined /> <span dir="ltr">{lead.chiefExecutivePhone || '—'}</span></Space>
            </Descriptions.Item>
            <Descriptions.Item label="زمینه فعالیت">{lead.industry || '—'}</Descriptions.Item>
            <Descriptions.Item label="سمت شخص رابط">{lead.jobTitle || '—'}</Descriptions.Item>
            <Descriptions.Item label="بودجه احتمالی">{lead.budgetAmount ? `${formatNumber(lead.budgetAmount)} ${lead.budgetCurrency || ''}` : '—'}</Descriptions.Item>
            <Descriptions.Item label="شخص رابط">{lead.contactPersonName || '—'}</Descriptions.Item>
            <Descriptions.Item label="شماره شخص رابط">
              <Space><PhoneOutlined /> <span dir="ltr">{lead.contactPersonPhone || '—'}</span></Space>
            </Descriptions.Item>
            <Descriptions.Item label="شماره موبایل">
              <Space><PhoneOutlined /> <span dir="ltr">{lead.mobile || '—'}</span></Space>
            </Descriptions.Item>
            <Descriptions.Item label="ایمیل"><span dir="ltr">{lead.email || '—'}</span></Descriptions.Item>
            <Descriptions.Item label="منبع جذب">{lead.source || '—'}</Descriptions.Item>
            <Descriptions.Item label="شناسه مسئول"><span className="mono-text">{lead.ownerMembershipId || 'بدون مسئول'}</span></Descriptions.Item>
            <Descriptions.Item label="شناسه تیم"><span className="mono-text">{lead.ownerTeamId || '—'}</span></Descriptions.Item>
            <Descriptions.Item label="شعبه"><span className="mono-text">{lead.branchId || '—'}</span></Descriptions.Item>
            <Descriptions.Item label="تاریخ ثبت">{formatDateTime(lead.createdAtUtc)}</Descriptions.Item>
            <Descriptions.Item label="آخرین تغییر">{formatDateTime(lead.updatedAtUtc)}</Descriptions.Item>
            <Descriptions.Item label="توضیحات">{lead.description || 'توضیحی ثبت نشده است.'}</Descriptions.Item>
          </Descriptions>

          <div className="technical-metadata">
            <Typography.Text type="secondary">شناسه سرنخ</Typography.Text>
            <Typography.Text copyable className="mono-text">{lead.id}</Typography.Text>
            <Typography.Text type="secondary">ETag / RowVersion</Typography.Text>
            <Typography.Text copyable className="mono-text">{detail.etag}</Typography.Text>
          </div>

          {lead.status === 'converted' ? <Card size="small" title="خروجی تبدیل"><Descriptions column={1} size="small"><Descriptions.Item label="مخاطب"><Typography.Text copyable>{lead.convertedContactId || '—'}</Typography.Text></Descriptions.Item><Descriptions.Item label="سازمان"><Typography.Text copyable>{lead.convertedAccountId || '—'}</Typography.Text></Descriptions.Item><Descriptions.Item label="فرصت فروش"><Typography.Text copyable>{lead.convertedOpportunityId || '—'}</Typography.Text></Descriptions.Item></Descriptions></Card> : null}

          <Card size="small" title="تاریخچه وضعیت" loading={history.isLoading}>
            <Timeline items={(history.data || []).map(item => ({ color: item.toStatus === 'disqualified' ? 'red' : item.toStatus === 'converted' ? 'green' : 'blue', content: <div><Space><LeadStatusTag status={item.toStatus} /><Typography.Text type="secondary">{formatDateTime(item.occurredAtUtc)}</Typography.Text></Space><br /><Typography.Text>{item.reason || 'بدون توضیح'}</Typography.Text></div> }))} />
            {!history.isLoading && !history.data?.length ? <Typography.Text type="secondary">هنوز تغییری ثبت نشده است.</Typography.Text> : null}
          </Card>

          <Card size="small" title="رفتار و تعامل‌های امتیازدهی" loading={engagements.isLoading}>
            <Timeline items={(engagements.data || []).map(item => ({ color: item.eventType === 'meeting-scheduled' ? 'green' : item.eventType === 'contacted' ? 'blue' : 'gray', content: <div><Space><Tag>{item.eventType}</Tag><Typography.Text type="secondary">{formatDateTime(item.occurredAtUtc)}</Typography.Text></Space>{item.note ? <Typography.Paragraph className="!mb-0 !mt-1" type="secondary">{item.note}</Typography.Paragraph> : null}</div> }))} />
            {!engagements.isLoading && !engagements.data?.length ? <Typography.Text type="secondary">هنوز تعامل قابل‌امتیازدهی ثبت نشده است.</Typography.Text> : null}
          </Card>

          <Card size="small" title="فعالیت‌های مرتبط" loading={activities.isLoading}>
            <Timeline items={(activities.data?.items || []).map(item => ({ color: item.status === 'completed' ? 'green' : 'blue', content: <div><Space><Tag>{item.type}</Tag><Typography.Text strong>{item.subject}</Typography.Text></Space><br /><Typography.Text type="secondary">{item.dueAtUtc ? `سررسید: ${formatDateTime(item.dueAtUtc)}` : formatDateTime(item.createdAtUtc)}</Typography.Text></div> }))} />
            {!activities.isLoading && !activities.data?.items.length ? <Typography.Text type="secondary">فعالیتی برای این سرنخ ثبت نشده است.</Typography.Text> : null}
          </Card>
        </Flex>
      ) : null}
      {lead ? <LeadLifecycleModal mode={lifecycleMode} lead={lead} onClose={() => setLifecycleMode(null)} /> : null}
    </Drawer>
  );
};
